// src/components/Sidebar.jsx

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaUserEdit, FaStore, FaUsers, FaTruck, FaMoneyBill, FaPrint,
  FaCommentDots, FaSignOutAlt, FaGlobe, FaUtensils, FaFileInvoice,
  FaQrcode, FaChartPie, FaBoxOpen, FaBars, FaBell, FaEnvelope, FaTimes,
  FaChevronDown, FaChevronRight
} from "react-icons/fa";
import logo from "../../src/assets/logiscreen.png";
import LogoIcon from "../assets/Logo";
import { GrProjects } from "react-icons/gr";
import BusinessModal from "../components/restaurant/restaurants/BusinessModal";
import { logoutUser } from "../helper/generalHelper";

const SidebarAdmin = ({ isOpen, onClose }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const menuItems = [
    { label: "Inventory", icon: <FaBoxOpen />, path: "/admin/inventory" },
    {
      label: "Restaurant",
      icon: <FaBoxOpen />,
      path: "/admin/restaurant",
      subMenu: [
        { label: "Business", path: "/admin/restaurant/business" },
        // { label: "Branches", path: "/admin/restaurant/branches" },
        { label: "Menu Categories", path: "/admin/restaurant/menu-categories" },
        { label: "Tables", path: "/admin/restaurant/tables" },
      ],
    },
    { label: "Customer", icon: <FaUsers />, path: "/admin/customers" },
    { label: "Vendor", icon: <FaUsers />, path: "/admin/vendors" },
    { label: "Staff", icon: <FaUsers />, path: "/admin/staff" },
    // { label: "Delivery", icon: <FaTruck />, path: "/admin/delivery" },
    { label: "Expense / Income", icon: <FaMoneyBill />, path: "/admin/expenseincome" },
    { label: "Print Management", icon: <FaPrint />, path: "/admin/print-management" },
  ];

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  return (
    <>
      {/* ✅ Mobile Top Navbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow flex items-center justify-between px-4 py-2">
        <img src={logo} alt="Logo" className="h-8" />
        <div className="flex items-center gap-4 text-gray-700 text-lg">
          <FaEnvelope className="cursor-pointer hover:text-rose-600" />
          <FaBell className="cursor-pointer hover:text-rose-600" />
          <button onClick={() => setMobileMenuOpen(true)}>
            <FaBars />
          </button>
        </div>
      </div>

      {/* ✅ Sidebar (desktop or mobile slide-in) */}
      <div
        className={`fixed top-0 left-0 md:h-full h-[95%] overflow-scroll w-72 bg-white shadow-lg z-50 transition-transform ${
          isOpen || mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:translate-x-0`}
      >
        {/* Mobile Close Button */}
        <div className="md:hidden flex justify-end p-2">
          <button onClick={() => setMobileMenuOpen(false)} className="text-gray-700">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="p-4 border-b">
          <LogoIcon />
          <h1 className="text-lg font-bold mt-3">Stellaris</h1>
          <p className="text-sm text-gray-500">Serve More. Stress Less.</p>
        </div>
        <div className="p-4 space-y-2">
          {/* Business Section */}
          <Link to="/admin/editbusiness" className="flex items-center gap-2">
            <FaStore className="text-gray-600" />
            <div>
              <p className="text-sm font-bold">AZZ</p>
              <p className="text-xs text-gray-500">+91 9852312546</p>
            </div>
          </Link>

          <div className="flex gap-2 mt-2">
            <button onClick={() => setModalOpen(true)} className="bg-primary text-white text-xs px-3 py-1 rounded">
              Switch Business
            </button>
            <Link to="/admin/addbusiness" className="bg-pink-100 text-primary text-xs px-3 py-1 rounded">
              Create Business
            </Link>
          </div>

          <div className="w-full my-2">
            <Link
              to="/admin/subscriptionplans"
              className="w-full flex justify-center items-center gap-4 py-2 px-2 bg-stone-950 text-white text-xs px-3 py-1 rounded"
            >
              <span><GrProjects /></span> SubscriptionPlans
            </Link>
          </div>

          {/* Profile */}
          <div className="mt-4">
            <p className="font-semibold text-sm">Profile</p>
            <Link to="/admin/profile" className="flex items-center gap-2 mt-2">
              <FaUserEdit className="text-gray-600" />
              <div>
                <p className="text-sm font-bold">Stellaries</p>
                <p className="text-xs text-gray-500">Stellaries@gmail.com</p>
              </div>
            </Link>
          </div>

          <Link
            to="/admin/customer-qr"
            className="text-sm p-2 rounded flex items-center gap-2 mt-3"
          >
            <FaQrcode /> Customer QR Code
          </Link>

          {/* Management */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Management</p>
            {menuItems.map((item, i) => (
              <div key={i}>
                <button
                  onClick={() => item.subMenu && toggleDropdown(i)}
                  className={`w-full flex items-center justify-between text-sm p-2 rounded ${
                    location.pathname.startsWith(item.path)
                      ? "bg-rose-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {item.icon} {item.label}
                  </div>
                  {item.subMenu &&
                    (openDropdown === i ? <FaChevronDown /> : <FaChevronRight />)}
                </button>

                {item.subMenu && openDropdown === i && (
                  <div className="ml-6 mt-1 space-y-1">
                    {item.subMenu.map((sub, j) => (
                      <Link
                        key={j}
                        to={sub.path}
                        className={`block text-sm p-2 rounded ${
                          location.pathname === sub.path
                            ? "bg-rose-500 text-white"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Others */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Others</p>
            {[
              { label: "Help Chat", icon: <FaCommentDots />, path: "/admin/help" },
              { label: "Web Login", icon: <FaChartPie />, path: "/admin/web-login" },
              { label: "Feedback", icon: <FaCommentDots />, path: "/admin/feedback" },
            ].map((item, i) => (
              <Link
                to={item.path}
                key={i}
                className={`flex items-center gap-2 text-sm p-2 rounded ${
                  location.pathname === item.path
                    ? 'bg-rose-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.icon} {item.label}
              </Link>
            ))}
          </div>

          {/* Apps */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Apps</p>
            <Link
              to="/admin/orders"
              className={`flex items-center gap-2 text-sm p-2 rounded ${
                location.pathname === "/admin/orders"
                  ? 'bg-rose-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaUtensils /> Kitchen Display
            </Link>
          </div>

          {/* Settings */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Settings</p>
            <div className="space-y-1">
              {[
                // { label: "Receipt", icon: <FaFileInvoice />, path: "/admin/receipt" },
                { label: "Business", icon: <FaStore />, path: "/admin/business-settings" },
                { label: "Language - English", icon: <FaGlobe />, path: "/admin/language" },
              ].map((item, i) => (
                <Link
                  to={item.path}
                  key={i}
                  className={`flex items-center gap-2 text-sm p-2 rounded ${
                    location.pathname === item.path
                      ? 'bg-rose-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.icon} {item.label}
                </Link>
              ))}
              <div
                onClick={() => logoutUser(false)}
                className="flex items-center justify-between text-sm p-2 rounded cursor-pointer transition-all text-gray-700 hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  <FaSignOutAlt />
                  <span>Logout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BusinessModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      </div>
    </>
  );
};

export default SidebarAdmin;
